<template>
    <WidgetSection
        ref="WidgetSection"
        :loaded="loaded"
        :loading="loading"
        v-if="!hide"
        :defaultToggle="defaultToggle"
        @toggle="handleToggle"
        @loading-start="handleLoadingStart"
        :cssClass="`slider-type-1 ${cssClassWidgetSection}`"
        loadingText="common.loading_1"
        :paywall="paywall"
        :cheapmium="cheapmium"
    >
        <template v-slot:section-title>
            <div class="title">
                {{ $t('components.prospect_vacancies.name')}}
            </div>
        </template>

        <template v-slot:section-actions>
            <div class="swiper-navigation">
                <div :class="`swiper-button-prev-${uniqueId}`"></div>
                <div :class="`swiper-button-next-${uniqueId}`"></div>
            </div>
        </template>

        <template v-slot:section-content>
            <div class="row row-slider">
                <swiper-container ref="swiper" class="swiper-container" init="false">
                    <swiper-slide v-for="(vacancy, index) in vacancies.details" :key="index">
                        <component :is="vacancy.url ? 'a' : 'div'" :href="vacancy.url || undefined" target="_blank" rel="noreferrer external" class="slide-container">
                            <div class="bar">
                                <div class="left">
                                    <CompanyLogo 
                                        :domain="vacancy.domain ? vacancy.domain : null"
                                        :company_name="vacancy.company ? vacancy.company : vacancy.domain ? vacancy.domain : null"
                                    />
                                </div>
                                <div class="right">
                                    <div class="title" v-if="vacancy.vacancy || vacancy.title" v-html="highlightDescription(vacancy.vacancy ? vacancy.vacancy : vacancy.title)"></div>
                                    <div class="date" v-if="vacancy.date">{{ formatDate(vacancy.date) }}</div>
                                </div>
                            </div>
                            <div class="description vacancy" v-html="highlightDescription(vacancy.description || '')"></div>
                            <div class="url" v-if="vacancy.url">{{ vacancy.url }}</div>
                            <div class="status" v-else>{{ $t('components.prospect_vacancies.expired') }}</div>
                        </component>
                    </swiper-slide>
                </swiper-container>
            </div>
        </template>
    </WidgetSection>
</template>

<script>
    import { useProspectStore } from '@/stores/prospect';
    import { useAccountStore } from '@/stores/account';
    import { fetchUrl } from '@/services/ppApi';
    import { nextTick } from 'vue';
    import WidgetSection from '@/components/sections/WidgetSection';
    import CompanyLogo from '@/components/elements/misc/CompanyLogo';
    import i18n from "@/plugins/i18n";
    import { useToastStore } from '@/stores/utils/toast';

    export default {
        name: "ProspectApps",
        components: {
            WidgetSection,
            CompanyLogo
        },
        props: {
            auto: {
                type: Boolean,
                default: false
            },
            defaultToggle: {
                type: Boolean,
                default: false
            },
            paywall: {
                type: Boolean,
                default: false
            },
            cssClassWidgetSection: {
                type: String,
                required: false
            },
            narrowVersion: {
                type: Boolean,
                default: false
            },
            showLoadingToasts: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                loading: false,
                loaded: false,
                abortController: null,
                timeoutId: null,
                uniqueId: Math.random().toString(36).substr(2, 9),
                isActive: true
            }
        },
        computed: {
            cheapmium() {
                return useAccountStore().product.includes('ma_up');
            },
            vacancies() {
                return useProspectStore().vacancies;
            },
            paywallActive() {
                return this.paywall && (useAccountStore().paywall || this.cheapmium);
            },
            hide() {
                return !this.vacancies || (this.loaded && (!this.vacancies.details || this.vacancies.details.length < 1));
            }
        },
        methods: {
            highlightDescription(text) {
                return text.replace(/\*\*(.*?)\*\*/g, '<span class="highlight">$1</span>');
            },
            async fetchVacancy() {
                if (!this.vacancies || (this.vacancies.api_urls && this.vacancies.api_urls.length < 1) || this.loaded) {
                    return;
                }

                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }

                this.abortController = new AbortController();
                this.loading = true;

                try {
                    const response = await fetchUrl(this.vacancies.api_urls[0], this.abortController.signal);
                    this.vacancies.details = response.data.vacancies;

                    if(this.showLoadingToasts) {
                        if(this.vacancies?.details?.length > 0) {
                            useToastStore().addToast(i18n.global.t(`toasts.success.prospect_vacancies`), 'success');
                        } else {
                            useToastStore().addToast(i18n.global.t(`toasts.error.prospect_vacancies`), 'error'); 
                        }
                    }
                } catch (error) {
                    if (error?.code !== 'ERR_CANCELED') {
                        this.vacancies?.details ? this.vacancies.details = null : '';
                    }
                } finally {
                    this.loading = false;
                    this.loaded = true;
                    this.abortController = null;
                    await nextTick();
                    if (this.isActive) {
                        this.initSwiper();
                    }
                }
            },
            initSwiper() {
                if(!this.$refs.swiper) { return; }

                const swiperEl = this.$refs.swiper;
                const swiperParams = {
                    slidesPerView: 1,
                    spaceBetween: 24,
                    navigation: {
                        nextEl: `.swiper-button-next-${this.uniqueId}`,
                        prevEl: `.swiper-button-prev-${this.uniqueId}`
                    }
                };

                swiperParams.breakpoints =  this.narrowVersion ?
                                            {
                                                769: {
                                                    slidesPerView: 1.2,
                                                    slidesPerGroup: 1.2
                                                },
                                                1240: {
                                                    slidesPerView: 2,
                                                    slidesPerGroup: 2
                                                }
                                            } : {
                                                769: {
                                                    slidesPerView: 2,
                                                    slidesPerGroup: 2
                                                },
                                                992: {
                                                    slidesPerView: 3,
                                                    slidesPerGroup: 3
                                                }
                                            };

                const existing = swiperEl.swiper;
                if (existing && !existing.destroyed) {
                    existing.update();
                    existing.navigation?.update();
                    return;
                }

                if (existing && existing.destroyed) {
                    swiperEl.swiper = undefined;
                    swiperEl.initialized = false;
                }
                
                Object.assign(swiperEl, swiperParams);
                swiperEl.initialize();
            },
            formatDate(date) {
                const [year, month, day] = date.split('-');
                return `${day}-${month}-${year}`;
            },
            handleToggle(isVisible) {
                const swiperEl = this.$refs.swiper;
                if (isVisible) {
                    this.$nextTick(() => {
                        this.initSwiper();
                    });
                } else if (swiperEl?.swiper && !swiperEl.swiper.destroyed) {
                    swiperEl.swiper.destroy(true, true);
                }
            },
            async handleLoadingStart(isVisible) {
                if(this.paywallActive) { return; }

                if (!this.loaded) {
                    await this.fetchVacancy();
                    if(!this.hide && this.$refs.WidgetSection) {
                        this.$refs.WidgetSection.toggleSection();
                    }
                } else {
                    this.handleToggle(isVisible);
                }
            }
        },
        mounted() {
            if(!this.paywallActive && !this.loaded && (this.auto || this.defaultToggle)) {
                this.loading = true;
                this.timeoutId = setTimeout(() => {
                    this.fetchVacancy();
                }, 300);
            }
        },
        beforeUnmount() {
            this.isActive = false;

            if (this.$refs.swiper?.swiper) {
                this.$refs.swiper.swiper.destroy(true, true);
            }

            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        }
    }
</script>